import React, { useEffect, useState } from "react";
import { FaStar, FaQuoteLeft } from "react-icons/fa";
import api from "../../services/api";
import { color, type, space, container, autoGrid, card, eyebrow } from "../../styles/tokens";

/**
 * Student testimonials, rendered from real course reviews.
 *
 * Deliberately not seeded with invented names and quotes: this ships on a
 * live site, so made-up testimonials would be fake social proof. When there
 * are no reviews yet the section renders nothing at all rather than showing
 * placeholder people.
 */
export default function Testimonials() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/reviews/recent?limit=6")
      .then(({ data }) => { if (alive) setReviews(data?.data || []); })
      .catch(() => { if (alive) setReviews([]); });
    return () => { alive = false; };
  }, []);

  if (reviews.length === 0) return null;

  return (
    <section style={S.wrap} aria-labelledby="testimonials-h">
      <div style={container}>
        <div style={{ textAlign: "center" }}>
          <span style={eyebrow}>In their words</span>
          <h2 id="testimonials-h" style={S.title}>What students say</h2>
          <p style={S.lead}>
            Reviews left by students on courses they finished.
          </p>
        </div>

        <div style={{ ...autoGrid(280, space.md), marginTop: space.lg }}>
          {reviews.map((r) => {
            const who = r.User?.name || r.User?.username || "A student";
            return (
              <figure key={r.id} style={S.card}>
                <FaQuoteLeft aria-hidden="true" style={S.quote} />

                <div style={S.stars} aria-label={`${r.rating} out of 5`}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <FaStar
                      key={n}
                      aria-hidden="true"
                      style={{ color: n <= r.rating ? "#F59E0B" : color.line, fontSize: 13 }}
                    />
                  ))}
                </div>

                <blockquote style={S.body}>
                  {r.title && <strong style={S.reviewTitle}>{r.title}</strong>}
                  {r.content}
                </blockquote>

                <figcaption style={S.who}>
                  <span style={S.avatar} aria-hidden="true">{who.charAt(0).toUpperCase()}</span>
                  <span style={{ minWidth: 0 }}>
                    <span style={S.name}>{who}</span>
                    {r.Course?.name && <span style={S.course}>{r.Course.name}</span>}
                  </span>
                </figcaption>
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const S = {
  wrap: { background: color.canvas, borderTop: `1px solid ${color.line}`, padding: `${space.section} 0` },
  title: { margin: 0, fontSize: type.h2, fontWeight: 800, color: color.ink, textWrap: "balance" },
  lead: { margin: `${space.sm} auto 0`, maxWidth: "48ch", fontSize: type.body, lineHeight: 1.65, color: color.muted },

  card: { ...card, display: "flex", flexDirection: "column", padding: space.md, position: "relative" },
  quote: { color: color.brandSoft, fontSize: 24, marginBottom: space.xs },
  stars: { display: "flex", gap: 2, marginBottom: space.xs },
  body: {
    margin: 0,
    fontSize: type.small,
    lineHeight: 1.7,
    color: color.body,
    overflowWrap: "break-word",
  },
  reviewTitle: { display: "block", color: color.ink, marginBottom: 4, fontSize: "14.5px" },

  who: {
    display: "flex", alignItems: "center", gap: "10px",
    marginTop: "auto", paddingTop: space.md,
    borderTop: `1px solid ${color.lineSoft}`,
  },
  avatar: {
    display: "grid", placeItems: "center", width: 34, height: 34, flexShrink: 0,
    borderRadius: "50%", background: color.brand, color: "#fff",
    fontSize: 14, fontWeight: 700,
  },
  name: { display: "block", fontSize: type.small, fontWeight: 700, color: color.ink },
  course: { display: "block", fontSize: type.micro, color: color.muted },
};
